import { Bound } from '@blocksuite/affine/global/gfx';
import {
  type Color,
  DefaultTheme,
  NoteDisplayMode,
} from '@blocksuite/affine/model';
import type { BlockModel, Store } from '@blocksuite/affine/store';

import type { MindmapNodeType } from '../types';

/**
 * Display + organisation metadata for each typed card the picker
 * surfaces. Lives here (not in the deleted mindmap-helpers) so the
 * canvas, picker popover, and outline panel can share it via a
 * single import.
 *
 * `strokeColor` is the picker chip's border + the outline panel's
 * left-rail accent. The card itself uses `TYPE_BACKGROUND` for its
 * canvas-side note background.
 */
export interface NodeTypeMeta {
  label: string;
  description: string;
  branchLabel: string;
  fillColor: string;
  strokeColor: string;
  shortcut: string;
}

export const NODE_TYPE_REGISTRY: Record<MindmapNodeType, NodeTypeMeta> = {
  finding: {
    label: 'Finding',
    description: 'A result or claim extracted from the source',
    branchLabel: 'Findings',
    fillColor: '#E6EEF6',
    strokeColor: '#002B57',
    shortcut: 'F',
  },
  hypothesis: {
    label: 'Hypothesis',
    description: 'A claim or proposal the user wants to test',
    branchLabel: 'Hypotheses',
    fillColor: '#FFF4DB',
    strokeColor: '#B45309',
    shortcut: 'H',
  },
  question: {
    label: 'Question',
    description: 'An open question or gap to investigate later',
    branchLabel: 'Open Questions',
    fillColor: '#F1E8FB',
    strokeColor: '#6D28D9',
    shortcut: 'Q',
  },
  method: {
    label: 'Method',
    description: 'A technique, protocol, or experimental procedure',
    branchLabel: 'Methods',
    fillColor: '#E1F3E5',
    strokeColor: '#15803D',
    shortcut: 'M',
  },
  quote: {
    label: 'Quote',
    description: 'A verbatim passage worth preserving',
    branchLabel: 'Quotes',
    fillColor: '#F2F2F4',
    strokeColor: '#475569',
    shortcut: 'U',
  },
};

/** Canvas-side background per type — uses BlockSuite's named palette so the
 *  cards pick up the right edgeless theme colors automatically. The
 *  picker chips in the popover and the outline-panel rows share the
 *  same key so the visual language is consistent everywhere. */
const NOTE_BG = DefaultTheme.NoteBackgroundColorMap;

export const TYPE_BACKGROUND: Record<MindmapNodeType, Color> = {
  finding: NOTE_BG.Blue,
  hypothesis: NOTE_BG.Yellow,
  question: NOTE_BG.Purple,
  method: NOTE_BG.Green,
  quote: NOTE_BG.Grey,
};

// Fresh-captures column geometry. New cards stack downward at FRESH_X.
// Once a user drags a card OUT of this x-band it stops contributing to
// the stack — that respects manual organisation without bookkeeping.
export const FRESH_X = 80;
export const FRESH_Y_START = 80;
export const CARD_W = 360;
export const CARD_H = 220;
export const CARD_GAP = 24;
const FRESH_X_TOLERANCE = 60;

/**
 * All `affine:note` blocks on the doc whose `displayMode` is
 * `EdgelessOnly` — i.e. our Research-Canvas cards. The page-mode notes
 * (regular paragraphs the user types in the doc body) are excluded so
 * the outline panel only sees cards we created.
 */
export function findCardNotes(doc: Store): BlockModel[] {
  const blocks = doc.getBlocksByFlavour('affine:note');
  return blocks
    .map(b => b.model)
    .filter(model => {
      const props = (model as { props?: { displayMode?: string } }).props;
      return props?.displayMode === NoteDisplayMode.EdgelessOnly;
    });
}

/**
 * Compute where the next fresh-capture card should land on the doc's
 * surface. Only cards currently in the fresh column (within an x-band
 * around FRESH_X) contribute to the stack — cards the user has
 * dragged into a cluster are ignored on purpose so new captures keep
 * landing in a predictable place.
 */
export function nextFreshPosition(doc: Store): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const cards = findCardNotes(doc);
  let maxBottom = FRESH_Y_START - CARD_GAP;
  for (const card of cards) {
    const xywh = (card as { props?: { xywh?: string } }).props?.xywh;
    if (!xywh) continue;
    try {
      const bound = Bound.deserialize(xywh);
      if (Math.abs(bound.x - FRESH_X) <= FRESH_X_TOLERANCE) {
        const bottom = bound.y + bound.h;
        if (bottom > maxBottom) maxBottom = bottom;
      }
    } catch {
      /* malformed xywh — skip */
    }
  }
  return { x: FRESH_X, y: maxBottom + CARD_GAP, w: CARD_W, h: CARD_H };
}

/**
 * Build the markdown body of a card. We render the type as a bold tag
 * up top, the selection as a markdown blockquote (so BlockSuite's
 * adapter creates a real quote block), then the source-attribution
 * line and a blank line where the user can write their own notes.
 */
export function buildCardMarkdown(opts: {
  typeLabel: string;
  passage: string;
  sourceTitle?: string;
  /** When present, render a clickable `[Source ↗]` link back to the
   *  source block. The CanvasLinkRouter intercepts the click and
   *  scrolls the doc to the block (with a flash highlight). */
  sourceHref?: string;
  citationTokens?: Array<{
    type: 'pubmed' | 'doi' | 'arxiv';
    identifier: string;
  }>;
}): string {
  const passage = opts.passage.replace(/\s+/g, ' ').trim();
  const cites = (opts.citationTokens ?? [])
    .map(t => {
      switch (t.type) {
        case 'pubmed':
          return `[PMID:${t.identifier}]`;
        case 'doi':
          return `[DOI:${t.identifier}]`;
        case 'arxiv':
          return `[arXiv:${t.identifier}]`;
        default:
          return '';
      }
    })
    .filter(Boolean)
    .join(' ');

  const titlePart = opts.sourceTitle ? `*From:* ${opts.sourceTitle}` : '';
  const citationLine = cites ? `*Citations:* ${cites}` : '';
  // Render the back-link on its own line so it's visually distinct
  // from the "From" attribution. Markdown link syntax with a labelled
  // emoji makes it obvious this is a clickable affordance, not flavor
  // text. The custom URL scheme is preserved by BlockSuite's markdown
  // adapter — the CanvasLinkRouter intercepts the click globally.
  const backLink = opts.sourceHref
    ? `🔗 [Jump to source passage](${opts.sourceHref})`
    : '';

  return [
    `**${opts.typeLabel.toUpperCase()}**`,
    '',
    `> ${passage.replace(/\n/g, '\n> ')}`,
    '',
    titlePart,
    citationLine,
    backLink,
    '',
    '_Your notes…_',
  ]
    .filter(Boolean)
    .join('\n');
}
